// localStorage persistence for listCache namespaces. Every operation is
// defensive: corrupt JSON clears the key, quota errors drop the namespace,
// and any failure silently degrades the cache to in-memory only.

export const PERSIST_KEY_PREFIX = 'listCache';
export const MAX_PERSIST_BYTES = 300_000;

function storageKey(namespace) {
  return `${PERSIST_KEY_PREFIX}_${namespace}`;
}

/**
 * Load a namespace's entries, dropping any that are already TTL-expired.
 * @param {string} namespace
 * @param {number} ttlMs
 * @returns {Array<[string, {data: any, ts: number}]> | null}
 */
export function loadNamespace(namespace, ttlMs) {
  try {
    const raw = localStorage.getItem(storageKey(namespace));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      removeNamespace(namespace);
      return null;
    }
    const now = Date.now();
    return parsed.filter(
      ([key, entry]) =>
        typeof key === 'string' &&
        entry &&
        typeof entry.ts === 'number' &&
        now - entry.ts < ttlMs
    );
  } catch (e) {
    console.error(`Failed to load list cache "${namespace}":`, e);
    removeNamespace(namespace);
    return null;
  }
}

/**
 * Persist a namespace's entries. Oversized payloads are skipped; quota
 * errors remove the namespace so future writes can retry cleanly.
 * @param {string} namespace
 * @param {Array<[string, {data: any, ts: number}]>} entries
 */
export function saveNamespace(namespace, entries) {
  try {
    const serialized = JSON.stringify(entries);
    if (serialized.length > MAX_PERSIST_BYTES) return;
    localStorage.setItem(storageKey(namespace), serialized);
  } catch (e) {
    console.error(`Failed to save list cache "${namespace}":`, e);
    removeNamespace(namespace);
  }
}

export function removeNamespace(namespace) {
  try {
    localStorage.removeItem(storageKey(namespace));
  } catch (e) {
    console.error(`Failed to remove list cache "${namespace}":`, e);
  }
}
